import { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { api } from '../lib/api'
import { Clock, User, Calendar, FileText } from 'lucide-react'
import toast from 'react-hot-toast'

interface MembershipHistoryProps {
  membershipId?: number
  studentId?: number
  unitType?: string
  unitId?: number
  onClose?: () => void
}

export default function MembershipHistory({ 
  membershipId, 
  studentId, 
  unitType,
  unitId,
  onClose 
}: MembershipHistoryProps) {
  const [selectedMembership, setSelectedMembership] = useState<number | null>(membershipId || null)

  // Fetch memberships for unit if unitId is provided
  const { data: unitMembershipsData } = useQuery(
    ['unit-memberships', unitType, unitId],
    () => {
      if (unitType && unitId) {
        return api.get(`/memberships/units/${unitType}/${unitId}/members`).then(res => res.data)
      }
      return Promise.resolve({ data: [] })
    },
    {
      enabled: !!unitType && !!unitId && !membershipId && !studentId
    }
  )

  // Fetch history
  const { data: historyData, isLoading } = useQuery(
    ['membership-history', selectedMembership, studentId, unitType, unitId],
    () => {
      if (selectedMembership) {
        return api.get(`/memberships/${selectedMembership}/history`).then(res => res.data)
      } else if (studentId) {
        return api.get(`/memberships/students/${studentId}/history`, {
          params: { unit_type: unitType }
        }).then(res => res.data)
      } else if (unitType && unitId) {
        // Fetch all memberships for this unit and aggregate history
        return api.get(`/memberships`, {
          params: { unit_type: unitType, unit_id: unitId }
        }).then(res => {
          // Get history for all memberships
          const memberships = res.data.data || []
          return Promise.all(
            memberships.map((m: any) =>
              api.get(`/memberships/${m.id}/history`).then(h => h.data.data || [])
            )
          ).then(histories => ({
            data: histories.flat()
          }))
        })
      }
      return Promise.resolve({ data: [] })
    },
    {
      enabled: !!selectedMembership || !!studentId || (!!unitType && !!unitId)
    }
  )

  const history = historyData?.data || []

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getChangeTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'created': 'Erstellt',
      'updated': 'Aktualisiert',
      'ended': 'Beendet',
      'cancelled': 'Storniert',
      'restored': 'Wiederhergestellt'
    }
    return labels[type] || type
  }

  const getChangeTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'created': 'bg-green-100 text-green-800',
      'updated': 'bg-blue-100 text-blue-800',
      'ended': 'bg-gray-100 text-gray-800',
      'cancelled': 'bg-red-100 text-red-800',
      'restored': 'bg-yellow-100 text-yellow-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Historie</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Keine Historie verfügbar</p>
        </div>
      ) : (
        <div className="space-y-4">
          {history.map((entry: any, index: number) => (
            <div
              key={entry.id || index}
              className="border-l-4 border-primary-500 pl-4 py-2 bg-gray-50 rounded-r"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${getChangeTypeColor(
                        entry.change_type
                      )}`}
                    >
                      {getChangeTypeLabel(entry.change_type)}
                    </span>
                    {entry.username && (
                      <span className="text-sm text-gray-600">
                        von {entry.first_name} {entry.last_name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(entry.changed_at)}
                    </div>
                  </div>

                  {entry.old_values && entry.new_values && (
                    <div className="mt-2 space-y-1 text-sm">
                      {Object.keys(JSON.parse(entry.new_values || '{}')).map((key) => {
                        const oldVal = JSON.parse(entry.old_values || '{}')[key]
                        const newVal = JSON.parse(entry.new_values || '{}')[key]
                        
                        if (oldVal === newVal) return null

                        return (
                          <div key={key} className="flex items-center gap-2">
                            <span className="font-medium text-gray-700">{key}:</span>
                            <span className="text-red-600 line-through">{String(oldVal || '')}</span>
                            <span>→</span>
                            <span className="text-green-600">{String(newVal || '')}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {entry.change_notes && (
                    <div className="mt-2 text-sm text-gray-600 italic">
                      {entry.change_notes}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

